import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import MainLayout from '../components/MainLayout';
import AutoRoleSetup from '../components/AutoRoleSetup';
import PermissionGate from '../components/auth/PermissionGate';

/**
 * Page pour l'initialisation automatique des rôles avancés
 */
const AutoRoleSetupPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Initialisation des Rôles Avancés
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Outil de configuration automatique du nouveau système de permissions
            </p>
          </div>

          {/* Vérification de sécurité basique */}
          <PermissionGate
            permissions={[
              { resource: 'settings', action: 'read' },
              { resource: 'roles', action: 'read' }
            ]}
            requireAll={false}
            fallback={
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
                <h2 className="text-xl font-semibold text-red-800 dark:text-red-200 mb-2">
                  Accès Refusé
                </h2>
                <p className="text-red-700 dark:text-red-300">
                  Vous n'avez pas les permissions nécessaires pour accéder à cet outil.
                </p>
              </div>
            }
          >
            <div className="space-y-6">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
                  Nouveau Système de Rôles Avancés
                </h2>
                <p className="text-green-700 dark:text-green-300">
                  Cet outil permet d'initialiser automatiquement tous les rôles du nouveau système
                  avec des permissions granulaires et une hiérarchie complète.
                </p>
              </div>

              <AutoRoleSetup />

              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  📋 Rôles qui seront créés
                </h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
                  <li>Super Administrateur (niveau 100)</li>
                  <li>Administrateur (niveau 90)</li>
                  <li>Direction (niveau 85)</li>
                  <li>Coordinateur (niveau 80)</li>
                  <li>Assistant Administratif (niveau 75)</li>
                  <li>Infirmier (niveau 78)</li>
                  <li>Assistant de Vie (niveau 70)</li>
                  <li>Aide-Soignant (niveau 72)</li>
                  <li>Auxiliaire Familial (niveau 68)</li>
                  <li>Aide à Domicile (niveau 65)</li>
                  <li>Accompagnateur (niveau 63)</li>
                  <li>Garde d'Enfant (niveau 62)</li>
                  <li>Garde Nocturne (niveau 66)</li>
                  <li>Garde de Jour (niveau 64)</li>
                  <li>Bénévole (niveau 50)</li>
                  <li>Bénéficiaire (niveau 30)</li>
                  <li>Visiteur (niveau 10)</li>
                </ul>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                  ⚠️ Important
                </h3>
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                  Après la migration vers le nouveau système, assurez-vous que tous les utilisateurs
                  ont été migrés avec les rôles appropriés. Le système utilisera automatiquement
                  le nouveau format de permissions une fois initialisé.
                </p>
              </div>
            </div>
          </PermissionGate>
        </div>
      </div>
    </MainLayout>
  );
};

export default AutoRoleSetupPage;
